import Content from "../../common/page/Content";
import SearchResultCard from "../../SearchListings/SearchResultCard";
import useScrollToManager from "../../../hooks/useScrollToManager";
import ContactCards from "../../SearchListings/ContactCards";
import PropertySummary, {
  OwnerContactInfo,
} from "../../common/PropertySummary";
import { ContactInfo, Listing } from "../../../types/listing";
import { DesktopSearchToolbar } from "../../SearchListings/searchToolbar/DesktopSearchToolbar";
import { useEffect, useMemo } from "react";
import { useInkedSendCounts } from "src/components/sendCards";
import {
  PrintType,
  SearchResultsFormatType,
  SelectedListingsType,
} from "src/components/SearchListings/useSearchListingsPage";
import NoListingsFound from "../../SearchListings/NoListingsFound";
import { SearchPageFooter } from "../../SearchListings/SearchPageFooter";
import ActiveFiltersPanel from "../../SearchListings/ActiveFiltersPanel";
import PrintOneView from "src/components/listings/print/PrintOneView";
import { PrintRenderer } from "src/components/SearchListings/PrintRenderer";
import PrintListView from "src/components/listings/print/PrintListView";
import Loading from "src/components/SearchListings/Loading";
import { BatchSelection } from "../../SearchListings/BatchSelection";
import VerticalDivider from "../../common/VerticalDivider";

interface ActiveFilter {
  label: string;
  delete?: () => void;
}


const List = ({
  listings,
  selectedListings,
  printContentRef,
  printType,
  isLoading,
  selectedListing,
  onListingClick,
  onPropertyOpenNotesClick,
  onPropertyTagsClick,
  onPrintSingleListingClick,
  onFavoriteClick,
  isVisible = false,
  onToggleContactIsDisabled,
  pageSize,
  currentPage,
  isBatchSelectionActive,
  toggleItemIsSelected,
  totalRecords,
  onAdjustFiltersClick,
  onClearFiltersClick,
  onSearchParamsUpdated,
  activeFilters,
  isHideDNCPhoneNumbers,
  selectedItems = new Set<string>(),
  isSelectAllChecked = false,
  toggleSelectAll,
  onListingsTypeClick,
  onBatchSelectionToggle,
  isFavoritesOnly,
  onFavoritesOnlyToggle,
  sendCardsTemplateCategory = "",
}: {
  printContentRef: React.RefObject<HTMLDivElement>;
  selectedListings: Listing[];
  printType: PrintType;
  isLoading: boolean;
  listings: Listing[];
  selectedListing: Listing | null | undefined;
  onListingClick: (listing: Listing) => void;
  onPropertyOpenNotesClick: (listing: Listing) => void;
  onPropertyTagsClick: (listing: Listing) => void;
  onPrintSingleListingClick?: (listing: Listing) => void;
  onFavoriteClick?: (listing: Listing) => void;
  onToggleContactIsDisabled: (
    contact: ContactInfo,
    isDisabled: boolean
  ) => void;
  isVisible?: boolean;
  pageSize: number;
  currentPage: number;
  isBatchSelectionActive: boolean;
  toggleItemIsSelected: (listingId: string) => void;
  totalRecords: number;
  onAdjustFiltersClick?: () => void;
  onClearFiltersClick?: () => void;
  onSearchParamsUpdated: (updates: Record<string, unknown>) => void;
  activeFilters?: ActiveFilter[];
  isHideDNCPhoneNumbers?: boolean | null | undefined;
  selectedItems?: Set<string>;
  isSelectAllChecked?: boolean;
  toggleSelectAll?: (checked: boolean) => void;
  onListingsTypeClick?: (type: SelectedListingsType) => void;
  onBatchSelectionToggle?: () => void;
  isFavoritesOnly?: boolean;
  onFavoritesOnlyToggle?: () => void;
  sendCardsTemplateCategory?: string;
}) => {
  const { addScrollToItem, scrollToItemWithId } = useScrollToManager();

  // Fetch send counts for current page listings using composite IDs
  const listingIds = useMemo(() => {
    return (listings || []).map((listing: Listing) => listing.id);
  }, [listings]);

  const product = listings?.[0]?.productType;
  const { data: sendCountsData } = useInkedSendCounts(
    product && listingIds.length > 0
      ? { product, ids: listingIds }
      : undefined
  );

  // Create a map of listing ID to send count
  const sendCountsMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (sendCountsData) {
      Object.entries(sendCountsData).forEach(([id, data]) => {
        map[id] = data.count;
      });
    }
    return map;
  }, [sendCountsData]);

  useEffect(() => {
    isVisible && selectedListing && scrollToItemWithId(selectedListing?.id);
  }, [isVisible, selectedListing, scrollToItemWithId]);

  const handlePropertyClick = (listing: Listing) => {
    onListingClick(listing);
    setTimeout(() => {
      // Prevent the sheet component from blocking mouse interactions
      document.body.style.pointerEvents = "";
    }, 100);
  };

  return (
    <Content>
      <div className="mb-2 px-[16px] md:px-[32px]">
        <DesktopSearchToolbar
          isSuppressSearchResultsFormatSelector
          isSuppressFilterButton
          isSuppressSortBy
          searchResultsFormat={SearchResultsFormatType.list}
          selectedPageListing={selectedListing || ({} as Listing)}
          listings={listings}
          selectedSort={""}
          onSearchParamsUpdated={onSearchParamsUpdated}
          currentFeature={undefined}
          nextFeature={() => { /* no-op */ }}
          isLastFeature={false}
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPage={currentPage}
          isBatchSelectionActive={isBatchSelectionActive}
          onBatchSelectionToggle={onBatchSelectionToggle}
          isFavoritesOnly={isFavoritesOnly}
          onFavoritesOnlyToggle={onFavoritesOnlyToggle}
        />
      </div>
      {(toggleSelectAll && onListingsTypeClick) || !!activeFilters?.length ? (
        <div className="mb-2 px-[16px] md:px-[32px]">
          <div className="flex flex-row gap-2 items-center">
            {toggleSelectAll && onListingsTypeClick && (
              <>
                <BatchSelection
                  isSelectAllChecked={isSelectAllChecked}
                  toggleSelectAll={toggleSelectAll}
                  onListingsTypeClick={onListingsTypeClick}
                  isBatchSelectionActive={isBatchSelectionActive}
                />
                {isBatchSelectionActive && !!activeFilters?.length && <VerticalDivider />}
              </>
            )}
            {!!activeFilters?.length && (
              <ActiveFiltersPanel activeFilters={activeFilters || []} onFilterClick={onAdjustFiltersClick} />
            )}
          </div>
        </div>
      ) : null}
      {isLoading && (
        <div className="px-[16px] md:px-[32px] pt-3">
          <Loading />
        </div>
      )}
      {(listings || []).map((listing, index) => {
        return (
          <SearchResultCard
            isSelected={selectedListing?.id === listing.id}
            key={`search-result-card-${index}`}
            className="px-[16px] md:px-[32px]"
            isBatchSelectionActive={isBatchSelectionActive}
            isChecked={selectedItems.has(listing.id)}
            onCheckedChange={() => toggleItemIsSelected(listing.id)}
            ref={(element) => {
              if (element) {
                addScrollToItem(element, listing.id, index);
              }
            }}
          >
            <PropertySummary
              suppressPrice
              isSuppressStatusInfo
              toolbarPlacement="title"
              listing={listing}
              onTitleClick={() => handlePropertyClick(listing)}
              onOpenNotesClick={() => onPropertyOpenNotesClick(listing)}
              onOpenTagsClick={() => onPropertyTagsClick(listing)}
              onPrintClick={onPrintSingleListingClick ? () => onPrintSingleListingClick(listing) : undefined}
              onFavoriteClick={onFavoriteClick ? () => onFavoriteClick(listing) : undefined}
              isTagsAndNotesEnabled
              sendCardsTemplateCategory={sendCardsTemplateCategory}
              sendCardCount={sendCountsMap[listing.id] || 0}
            />
            <OwnerContactInfo {...listing.ownerContactInfo} />
            <ContactCards
              isContactEditingDisabled
              onToggleContactIsDisabled={onToggleContactIsDisabled}
              contacts={(listing.contacts || []).filter(contact => 
                isHideDNCPhoneNumbers ? !contact.dnc : true
              )}
            />
          </SearchResultCard>
        );
      })}
      {!totalRecords && !isLoading && (
        <div className="px-[16px] md:px-[32px]">
          <NoListingsFound
            onAdjustFiltersClick={onAdjustFiltersClick}
            onClearFiltersClick={onClearFiltersClick}
          />
        </div>
      )}
      <SearchPageFooter
        totalRecords={totalRecords}
        pageSize={pageSize}
        currentPage={currentPage}
        gutterClassNames="px-[16px] md:px-[32px]"
        onSearchParamsUpdated={
          onSearchParamsUpdated ||
          (() => {
            /* no-op */
          })
        }
      />
      <section
        ref={printContentRef}
        className="hidden print:block m-4"
      >
        {selectedListings && (
          <PrintRenderer
            printType={printType}
            listings={selectedListings}
            PrintListViewComponentType={PrintListView}
            PrintOneViewComponentType={PrintOneView}
          />
        )}
      </section>
    </Content>
  );
};

export default List;
